import { cleanCell, normalizeName } from '../../data/transform'
import type { Organization } from '../../types/crm'

export type ProspectFieldKey =
  | 'companyName'
  | 'ownerName'
  | 'priority'
  | 'website'
  | 'facebook'
  | 'linkedin'
  | 'email'
  | 'phone'
  | 'city'
  | 'state'
  | 'source'
  | 'opportunityValue'
  | 'notes'

export type ProspectFormValues = Record<ProspectFieldKey, string>

export type ProspectOrganizationIndexEntry = Pick<Organization, 'name' | 'sheetRow'>

export type ProspectOptions = {
  writeEnabled: boolean
  organizations?: ProspectOrganizationIndexEntry[]
  priority: string[]
  type: string[]
  accountManager: string[]
  stage: string[]
  lossReason: string[]
  source: string[]
  status: string[]
  state: string[]
}

export type ProspectFieldErrors = Partial<Record<ProspectFieldKey, string>>

export type ProspectSubmission = Omit<ProspectFormValues, 'opportunityValue'> & {
  opportunityValue: number | null
}

export type ProspectReviewRow = {
  label: string
  value: string | number | null
}

export type ProspectReview = {
  existingOrganization: ProspectOrganizationIndexEntry | null
  organizationName: string
  organizationRows: ProspectReviewRow[] | null
  opportunityRows: ProspectReviewRow[]
  submission: ProspectSubmission
}

export const EMPTY_PROSPECT_FORM: ProspectFormValues = {
  companyName: '',
  ownerName: '',
  priority: '',
  website: '',
  facebook: '',
  linkedin: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  source: '',
  opportunityValue: '',
  notes: '',
}

const TEXT_LIMITS: Partial<Record<ProspectFieldKey, number>> = {
  companyName: 200,
  ownerName: 200,
  priority: 100,
  website: 2_048,
  facebook: 2_048,
  linkedin: 2_048,
  email: 254,
  phone: 50,
  city: 100,
  state: 100,
  source: 100,
  notes: 5_000,
}

const FIELD_LABELS: Record<ProspectFieldKey, string> = {
  companyName: 'Company name',
  ownerName: 'Owner name',
  priority: 'Priority',
  website: 'Website',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  email: 'Email',
  phone: 'Phone',
  city: 'City',
  state: 'State',
  source: 'Source',
  opportunityValue: 'Opportunity value',
  notes: 'Notes',
}

function normalizeText(value: string): string {
  return cleanCell(value.normalize('NFKC'))
}

const PLACEHOLDERS = new Set([
  '-',
  ' - ',
  '–',
  'na',
  'n/a',
  'none',
  'not available',
  'not provided',
  'not identified',
  'unknown',
])

function isPlaceholder(value: string): boolean {
  return PLACEHOLDERS.has(normalizeText(value).toLocaleLowerCase().replace(/[.!]+$/, ''))
}

function normalizeEmail(value: string): string {
  return normalizeText(value).toLocaleLowerCase()
}

export function normalizeSafeUrl(value: string): string {
  const trimmed = value.normalize('NFKC').trim()
  if (!trimmed || isPlaceholder(trimmed)) return ''
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmed) && !/^https?:/i.test(trimmed)) return ''

  try {
    const candidate = trimmed.startsWith('//')
      ? `https:${trimmed}`
      : /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const url = new URL(candidate)
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || !url.hostname.includes('.')) return ''
    return url.toString()
  } catch {
    return ''
  }
}

function parseOpportunityValue(value: string): number | null {
  const normalized = value.trim().replace(/[$,\s]/g, '')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeOptionText(value: string): string {
  return normalizeText(value).toLocaleLowerCase()
}

function includesOption(options: readonly string[], value: string): boolean {
  const normalizedValue = normalizeOptionText(value)
  return options.some((option) => normalizeOptionText(option) === normalizedValue)
}

export function validateProspectForm(values: ProspectFormValues, options: ProspectOptions): ProspectFieldErrors {
  const errors: ProspectFieldErrors = {}
  const companyName = normalizeText(values.companyName)
  const ownerName = normalizeText(values.ownerName)

  if ((!companyName || isPlaceholder(companyName)) && (!ownerName || isPlaceholder(ownerName))) {
    const message = 'Enter a company name or owner name.'
    errors.companyName = message
    errors.ownerName = message
  }

  for (const [key, limit] of Object.entries(TEXT_LIMITS) as Array<[ProspectFieldKey, number]>) {
    if (values[key].trim().length > limit) errors[key] = `${FIELD_LABELS[key]} is too long.`
    else if (values[key].trim() && isPlaceholder(values[key])) errors[key] = 'Enter a real value or leave this field blank.'
  }

  const priority = normalizeText(values.priority)
  if (!priority) errors.priority = 'Choose a priority.'
  else if (!includesOption(options.priority, priority)) errors.priority = 'Choose a current priority option.'

  const source = normalizeText(values.source)
  if (!source) errors.source = 'Choose a source.'
  else if (!includesOption(options.source, source)) errors.source = 'Choose a current source option.'

  const state = normalizeText(values.state)
  if (state && !includesOption(options.state, state)) errors.state = 'Choose a current state option.'

  const email = normalizeEmail(values.email)
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.'

  const phone = normalizeText(values.phone)
  if (phone) {
    const digitCount = phone.replace(/\D/g, '').length
    if (!/^[+()\d.\-\s]+$/.test(phone) || digitCount < 7 || digitCount > 15) {
      errors.phone = 'Enter a valid phone number.'
    }
  }

  for (const key of ['website', 'facebook', 'linkedin'] as const) {
    if (values[key].trim() && !normalizeSafeUrl(values[key])) errors[key] = 'Enter a safe http or https web address.'
  }

  const rawValue = values.opportunityValue.trim()
  const value = parseOpportunityValue(rawValue)
  if (rawValue && (value === null || value < 0 || value > 1_000_000_000_000)) {
    errors.opportunityValue = 'Enter a valid non-negative dollar value.'
  }

  return errors
}

export function normalizeProspectSubmission(values: ProspectFormValues, options: ProspectOptions): ProspectSubmission {
  const optionValue = (items: readonly string[], value: string) => (
    items.find((item) => normalizeOptionText(item) === normalizeOptionText(value)) ?? normalizeText(value)
  )

  return {
    companyName: normalizeText(values.companyName),
    ownerName: normalizeText(values.ownerName),
    priority: optionValue(options.priority, values.priority),
    website: normalizeSafeUrl(values.website),
    facebook: normalizeSafeUrl(values.facebook),
    linkedin: normalizeSafeUrl(values.linkedin),
    email: normalizeEmail(values.email),
    phone: normalizeText(values.phone),
    city: normalizeText(values.city),
    state: optionValue(options.state, values.state),
    source: optionValue(options.source, values.source),
    opportunityValue: parseOpportunityValue(values.opportunityValue),
    notes: normalizeText(values.notes),
  }
}

export function buildProspectReview(
  values: ProspectFormValues,
  options: ProspectOptions,
  organizations: readonly ProspectOrganizationIndexEntry[],
): ProspectReview {
  const submission = normalizeProspectSubmission(values, options)
  const requestedOrganizationName = submission.companyName || submission.ownerName
  const existingOrganization = organizations.find(
    (organization) => normalizeName(organization.name.normalize('NFKC')) === normalizeName(requestedOrganizationName.normalize('NFKC')),
  ) ?? null
  const organizationName = existingOrganization?.name || requestedOrganizationName
  const notes = submission.notes || null

  return {
    existingOrganization,
    organizationName,
    submission,
    organizationRows: existingOrganization ? null : [
      { label: 'B · Priority', value: submission.priority },
      { label: 'C · Name', value: organizationName },
      { label: 'D · Type', value: 'Prospect' },
      { label: 'E · Acct. Manager', value: 'William Jones' },
      { label: 'F · Website', value: submission.website || null },
      { label: 'G · FB Profile', value: submission.facebook || null },
      { label: 'H · Linkedin Profile', value: submission.linkedin || null },
      { label: 'I · Email', value: submission.email || null },
      { label: 'J · Phone', value: submission.phone || null },
      { label: 'K · City', value: submission.city || null },
      { label: 'L · State', value: submission.state || null },
      { label: 'M · Notes', value: notes },
    ],
    opportunityRows: [
      { label: 'B · Priority', value: submission.priority },
      { label: 'C · Name', value: submission.ownerName || 'Not Identified' },
      { label: 'D · Deal Owner', value: 'William Jones' },
      { label: 'E · Organization', value: organizationName },
      { label: 'F · Status', value: 'Open' },
      { label: 'G · Stage', value: 'Lead' },
      { label: 'H · Loss Reason', value: null },
      { label: 'I · Source', value: submission.source },
      { label: 'J · Value', value: submission.opportunityValue },
      { label: 'K · Probability', value: null },
      { label: 'L · Exp. Close', value: null },
      { label: 'M · Notes', value: notes },
    ],
  }
}
